import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import { object, reach } from 'yup';
import {
    NeglectedOrDelinquentInformationFormValues,
} from '../../../types/NeglectedOrDelinquentInformationModel';
import {
    AGE_FIELDS,
    GENDER_FIELDS,
    RACE_ETHNICITY_FIELDS,
} from '../../../types/NeglectedOrDelinquentStudentDemographicsFields';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import { PanelType } from '../../../types/PanelType';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const demographicTotal = (
    values: Record<string, string>,
    fields: { key: string }[]
): number | '' => {
    const counts = fields.map(({ key }) => values[key]);
    if (counts.some((value) => value === '' || !/^\d{1,6}$/.test(value))) return '';
    return counts.reduce((total, value) => total + Number(value), 0);
};

const NeglectedOrDelinquentStudentDemographicsPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    const raceTotal = demographicTotal(
        values.studentDemographics.raceEthnicity, RACE_ETHNICITY_FIELDS
    );
    const genderTotal = demographicTotal(
        values.studentDemographics.gender, GENDER_FIELDS
    );
    const ageTotal = demographicTotal(
        values.studentDemographics.age, AGE_FIELDS
    );

    const renderFields = (
        section: 'raceEthnicity' | 'gender' | 'age',
        fields: { key: string; label: string }[]
    ): ReactElement[] => fields.map(({ key, label }) => {
        const field = `studentDemographics.${section}.${key}`;
        const inputSchema = object().shape({
            [field]: reach(validationSchema, field),
        });
        return (
        <FormTextbox
            key={key}
            field={field}
            displayName={label}
            disabled={disabled}
            validationSchema={inputSchema}
        />
        );
    });

    const renderTotal = (
        field: string,
        displayName: string,
        value: number | ''
    ): ReactElement => (
        <div className="field-container disabled" key={field}>
            <FormInputLabel field={field} displayName={displayName} disabled />
            <input
                id={field}
                data-testid={field}
                className="data-input"
                type="text"
                disabled
                readOnly
                value={value}
            />
        </div>
    );

    return (
        <Panel panelType={PanelType.INFO} heading="Student Demographics">
            <section className="nord-demographics-section">
                <h3>Race/Ethnicity</h3>
                <div className="nord-demographics-race">
                    {renderFields('raceEthnicity', RACE_ETHNICITY_FIELDS)}
                    {renderTotal(
                        'raceEthnicityTotal',
                        'Total Students by Race/Ethnicity',
                        raceTotal
                    )}
                </div>
                {typeof errors.raceEthnicityTotal === 'string' && (
                    <p className="nord-demographics-error" role="alert">
                        {errors.raceEthnicityTotal}
                    </p>
                )}
            </section>
            <section className="nord-demographics-section">
                <h3>Gender</h3>
                <div className="nord-demographics-gender">
                    {renderFields('gender', GENDER_FIELDS)}
                    {renderTotal('genderTotal', 'Total Students by Gender', genderTotal)}
                </div>
                {typeof errors.genderTotal === 'string' && (
                    <p className="nord-demographics-error" role="alert">
                        {errors.genderTotal}
                    </p>
                )}
            </section>
            <section className="nord-demographics-section">
                <h3>Age</h3>
                <div className="nord-demographics-age">
                    {renderFields('age', AGE_FIELDS)}
                    {renderTotal('ageTotal', 'Total Students by Age', ageTotal)}
                </div>
                {typeof errors.ageTotal === 'string' && (
                    <p className="nord-demographics-error" role="alert">
                        {errors.ageTotal}
                    </p>
                )}
            </section>
        </Panel>
    );
};

export default NeglectedOrDelinquentStudentDemographicsPanel;
